import { useEffect, useRef } from 'react';

import { useSuperObjectClassListQuery } from '@/hook';
import { useShortcut } from '@/package';
import { useTaskFrameStore, useToolMenuStore } from '@/store';
import type { ObjectClass } from '@/type';

export const useFilterTaskSuperClassListWithChild = () => {
  const { selectedTask } = useTaskFrameStore();
  const { data: superObjectClassList } = useSuperObjectClassListQuery();

  const filteredSuperObjectClassList =
    superObjectClassList
      ?.filter(item => item.classType !== 'Unknown')
      .filter(
        item =>
          selectedTask?.extendClass?.some(
            extendClass => extendClass.id === item.id,
          ) ||
          selectedTask?.extendClass?.some(
            extendClass => extendClass.parentID === item.id,
          ),
      ) ?? [];

  const childFilteredSuperObjectClassList = filteredSuperObjectClassList.map(
    item => {
      return {
        ...item,
        extendClass: item.extendClass?.filter(extendClass =>
          selectedTask?.extendClass?.some(
            selectedExtendClass => selectedExtendClass.id === extendClass.id,
          ),
        ),
      };
    },
  );

  const isUnavaliableSuperClass = (superClass: ObjectClass) => {
    return (
      !selectedTask?.extendClass?.some(
        extendClass => extendClass.id === superClass.id,
      ) &&
      selectedTask?.extendClass?.some(
        extendClass => extendClass.parentID === superClass.id,
      )
    );
  };

  const isMoreThanOneAvaliableObjectClass = (superClass: ObjectClass) => {
    /* v8 ignore start */
    const extendClassLength = superClass.extendClass?.length ?? 0;
    /* v8 ignore end */
    if (isUnavaliableSuperClass(superClass)) {
      return extendClassLength > 1;
    }
    return !!extendClassLength;
  };

  return {
    filteredSuperObjectClassList: childFilteredSuperObjectClassList,
    isUnavaliableSuperClass,
    isMoreThanOneAvaliableObjectClass,
  };
};

export const useInitToolList = () => {
  const { initBoxToolList, isToolListInitialized, setIsToolListInitialized } =
    useToolMenuStore();
  const { selectedTask } = useTaskFrameStore();
  const { filteredSuperObjectClassList } =
    useFilterTaskSuperClassListWithChild();

  useEffect(() => {
    if (isToolListInitialized || !filteredSuperObjectClassList.length) return;

    initBoxToolList(filteredSuperObjectClassList);
  }, [filteredSuperObjectClassList, isToolListInitialized]);

  useEffect(() => {
    setIsToolListInitialized(false);
  }, [selectedTask]);
};

export const useRegisterShortcut = (
  extendClassWithToolHandler: ReturnType<typeof useExtendClassWithToolHandler>,
) => {
  const { filteredSuperObjectClassList } =
    useFilterTaskSuperClassListWithChild();
  const { namespace, register, unregister } = useShortcut();
  const { toolMap, findByName, select } = useToolMenuStore();
  const {
    getSelectedObjectClassInSuperClass,
    selectObjectClassToolCircularByParent,
  } = extendClassWithToolHandler;

  const genBoxTool = findByName('box')!;

  useEffect(() => {
    register(`labeling.labelBox.generationMode` as any, () => {
      if (!genBoxTool.isSelected) {
        select(genBoxTool.id);
        return;
      }

      const selectedSuperClass = filteredSuperObjectClassList
        .filter(superClass => !!getSelectedObjectClassInSuperClass(superClass))
        .at(0);

      if (selectedSuperClass) {
        selectObjectClassToolCircularByParent(selectedSuperClass);
      }
    });

    return () => {
      unregister(`labeling.labelBox.generationMode` as any);
    };
  }, [namespace, toolMap, filteredSuperObjectClassList, genBoxTool.isSelected]);

  useEffect(() => {
    filteredSuperObjectClassList.forEach((superClass, index) => {
      register(`labeling.labelBox.generationMode${index + 1}` as any, () => {
        if (!genBoxTool.isSelected) {
          select(genBoxTool.id);
          return;
        }

        selectObjectClassToolCircularByParent(superClass);
      });
    });

    return () => {
      filteredSuperObjectClassList.forEach((_, index) => {
        unregister(`labeling.labelBox.generationMode${index + 1}` as any);
      });
    };
  }, [namespace, toolMap, genBoxTool.isSelected, filteredSuperObjectClassList]);
};

export const useExtendClassWithToolHandler = () => {
  const {
    getSelectedToolMenuList,
    deselect,
    select,
    selectChildren,
    findByName,
    isToolListInitialized,
  } = useToolMenuStore();
  const { filteredSuperObjectClassList, isUnavaliableSuperClass } =
    useFilterTaskSuperClassListWithChild();
  const lastSelectedToolByParent = useRef<{
    [key: ObjectClass['id']]: ObjectClass['id'];
  }>({});

  useEffect(() => {
    if (
      !isToolListInitialized ||
      !!Object.keys(lastSelectedToolByParent.current).length
    )
      return;

    filteredSuperObjectClassList.forEach(item => {
      if (isUnavaliableSuperClass(item)) {
        const firstChild = item.extendClass?.at(0);
        if (!firstChild) return;
        lastSelectedToolByParent.current[item.id] = firstChild.id;
        return;
      }
      lastSelectedToolByParent.current[item.id] = item.id;
    });
  }, [isToolListInitialized, filteredSuperObjectClassList]);

  const deselectAllExtendClassTool = () => {
    getSelectedToolMenuList()
      .filter(tool => tool.name.match(/box\d+/))
      .forEach(tool => {
        deselect(tool.id);
      });
  };

  const selectToolAndUpdateHistory = (objectClass: ObjectClass) => {
    const targetTool = findByName(`box${objectClass.id}`);
    const parentTool =
      objectClass.classType === 'Super'
        ? findByName('box')
        : findByName(`box${objectClass.parentID}`);
    if (!targetTool || !parentTool) return;
    selectChildren(parentTool.id, targetTool.id);
    const parentID =
      objectClass.classType === 'Super' ? objectClass.id : objectClass.parentID;
    lastSelectedToolByParent.current[parentID] = objectClass.id;
  };

  const selectNewObjectClassTool = (objectClass: ObjectClass) => {
    deselectAllExtendClassTool();
    const baseTool = findByName('box');
    if (!baseTool) return;
    select(baseTool.id);
    selectToolAndUpdateHistory(objectClass);
  };

  const selectObjectClassToolCircularByParent = (superClass: ObjectClass) => {
    const alreadySelectedTool = getSelectedToolMenuList().find(tool =>
      tool.name.match(/box\d+/),
    );
    const alreadySelectedClassId = Number(
      alreadySelectedTool?.name.replace(/box/, ''),
    );
    const isInSuperClass =
      superClass.id === Number(alreadySelectedClassId) ||
      !!superClass.extendClass?.some(
        extendClass => extendClass.id === Number(alreadySelectedClassId),
      );

    deselectAllExtendClassTool();
    // 다음 놈 선택
    if (isInSuperClass) {
      // 만약 부모가 선택됐었으면
      if (alreadySelectedClassId === superClass.id) {
        // 만약 자식이 없으면 그냥 다시 자기 선택
        if (!superClass.extendClass?.length)
          selectNewObjectClassTool(superClass);
        // 자식이 있으면 첫 번째 자식 선택
        else selectNewObjectClassTool(superClass.extendClass?.[0]);
      }
      // 만약 자식이 선택됐었으면 다음 자식 선택
      else {
        // 만약 마지막 자식이고 선택가능한 부모가 있으면 선택

        if (
          alreadySelectedClassId ===
          superClass.extendClass?.[superClass.extendClass.length - 1].id
        ) {
          if (isUnavaliableSuperClass(superClass)) {
            const firstChild = superClass.extendClass?.at(0);
            // v8 ignore start
            // eslint-disable-next-line max-depth
            if (!firstChild) return;
            // v8 ignore end
            selectNewObjectClassTool(firstChild);
          } else selectNewObjectClassTool(superClass);
        }
        // 만약 다음 자식이 있으면 다음 자식 선택
        else {
          const prevIndex = superClass.extendClass?.findIndex(
            extendClass => extendClass.id === alreadySelectedClassId,
          );

          // 만약 이전에 선택된 애가 없으면 Do nothing
          if (prevIndex === -1 || typeof prevIndex !== 'number') {
            return;
          }
          const nextObjectClass = superClass.extendClass?.[prevIndex + 1];

          /* v8 ignore start */
          if (!nextObjectClass) {
            // eslint-disable-next-line max-depth
            if (!isUnavaliableSuperClass(superClass)) return;
            selectNewObjectClassTool(superClass);
            /* v8 ignore end */
          } else {
            selectNewObjectClassTool(nextObjectClass);
          }
        }
      }
    } else {
      const recentObjectClass = [
        superClass,
        ...(superClass.extendClass ?? []),
      ].find(
        objectClass =>
          objectClass.id === lastSelectedToolByParent.current[superClass.id],
      );

      // 최근 선택된 애가 없으면
      if (!recentObjectClass) {
        // 만약 부모가 선택 불가능하면 첫 번째 자식 선택
        if (isUnavaliableSuperClass(superClass)) {
          const firstChild = superClass.extendClass?.at(0);
          if (!firstChild) return;
          selectNewObjectClassTool(firstChild);
        } else {
          // 만약 부모가 선택 가능하면 부모 선택
          selectNewObjectClassTool(superClass);
        }
      } else {
        // 최근 선택된 애가 있으면 최근 선택된 애 선택
        selectNewObjectClassTool(recentObjectClass);
      }
    }
  };

  const getSelectedObjectClassInSuperClass = (superClass: ObjectClass) => {
    return [superClass, ...(superClass.extendClass ?? [])].find(objectClass => {
      return getSelectedToolMenuList().find(
        tool => tool.name === `box${objectClass.id}`,
      );
    });
  };

  const getRecentObjectClassInSuperClass = (superClass: ObjectClass) => {
    const parentID =
      superClass.classType === 'Super' ? superClass.id : superClass.parentID;
    return [superClass, ...(superClass.extendClass ?? [])].find(objectClass => {
      return objectClass.id === lastSelectedToolByParent.current[parentID];
    });
  };

  const getToShowObjectClass = (superClass: ObjectClass) => {
    return (
      getSelectedObjectClassInSuperClass(superClass) ??
      getRecentObjectClassInSuperClass(superClass) ??
      superClass
    );
  };

  const isSelectedObjectClass = (objectClass: ObjectClass) => {
    return !!getSelectedToolMenuList().find(
      tool => tool.name === `box${objectClass.id}`,
    );
  };

  const isRecentObjectClass = (objectClass: ObjectClass) => {
    const parentID =
      objectClass.classType === 'Super' ? objectClass.id : objectClass.parentID;
    return objectClass.id === lastSelectedToolByParent.current[parentID];
  };

  return {
    getRecentObjectClassInSuperClass,
    getSelectedObjectClassInSuperClass,
    getToShowObjectClass,
    isSelectedObjectClass,
    isRecentObjectClass,

    selectObjectClassTool: selectNewObjectClassTool,
    selectObjectClassToolCircularByParent,
  };
};
